import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  Grid,
  GridItem,
  FormErrorMessage,
  Checkbox,
  CheckboxGroup,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Radio,
  RadioGroup,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines } from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import { Save, Cancel } from "../themes/IconButtonSet";
import CustomDatePicker from "./CustomDatePicker";

const VaccineQuestionnaireForm = () => {
  const navigate = useNavigate();
  const { sessionId } = useAuth();
  const [formData, setFormData] = useState({
    FullName: "",
    Phone: "",
    Email: "",
    Gender: "",
    Age: "",
    Occupation: "",
    OtherOccupation: "",
    Income: "", // New field
    Education: "", // New field
    VaccineTypeID: "",
    LivestockTypeID: "",
    UsageDate: "",
    Remarks: "", // General remarks
    SelectedProducts: [],
    SatisfactionRatings: {
      ProductQuality: "",
      ProductSafety: "",
      PriceQuality: "",
      ConvenienceSpeed: "",
      DiseasePrevention: "", // Label changed in form
      ProductSuitability: "",
      OverallProductSatisfaction: "", // Label changed in form
    },
    ServiceSatisfactionRatings: {
      ReadyOfService: "", // New field
      KnowledgeOfService: "", // New field
      SpeedOfService: "",
      ProcessOfService: "", // New field
      InfoOfService: "", // New field
    },
    CompareSatisfactionRatings: { // New section
      ComfortOfCompare: "",
      KnowledgeOfCompare: "",
      PriceOfCompare: "",
    },
    SuggestionsProductQuality: "", // Specific suggestion for Product Quality
    SuggestionsPrice: "", // Specific suggestion for Price
    SuggestionsService: "", // Specific suggestion for Service
    SuggestionsPR: "", // Specific suggestion for PR
    Suggestions: "", // This is now "ข้อเสนอแนะอื่นๆ"
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [vaccineTypes, setVaccineTypes] = useState([]);
  const [livestockTypes, setLivestockTypes] = useState([]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/login");
    } else {
      fetchVaccineTypes();
      fetchLivestockTypes();
    }
  }, [sessionId, navigate]);

  // Fetch Vaccine Types
  const fetchVaccineTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineTypes(response.VaccinesList || []);
      }
    } catch (error) {
      console.error("Error fetching vaccine types:", error);
    }
  };

  // Fetch Livestock Types
  const fetchLivestockTypes = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLivestockTypes(response.LivestockTypeList || []);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleRadioChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
    validateField(name, value);
  };

  const handleProductChange = (selected) => {
    setFormData({ ...formData, SelectedProducts: selected });
    validateField("SelectedProducts", selected);
  };

  const handleSatisfactionChange = (criterion, value, type = "SatisfactionRatings") => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [criterion]: value,
      },
    }));
    // Validate the whole group of ratings
    validateField(type, { ...formData[type], [criterion]: value });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "FullName":
        if (!value) newErrors[name] = "กรุณากรอกชื่อ-นามสกุล";
        else delete newErrors[name];
        break;
      case "Phone":
        if (!value || !/^\d{10}$/.test(value)) newErrors[name] = "กรุณากรอกหมายเลขโทรศัพท์ 10 หลัก";
        else delete newErrors[name];
        break;
      case "Email":
        if (!value || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) newErrors[name] = "กรุณากรอกอีเมลที่ถูกต้อง";
        else delete newErrors[name];
        break;
      case "Gender":
        if (!value) newErrors[name] = "กรุณาเลือกเพศ";
        else delete newErrors[name];
        break;
      case "Age":
        if (!value) newErrors[name] = "กรุณาเลือกช่วงอายุ";
        else delete newErrors[name];
        break;
      case "Income": // New validation
        if (!value) newErrors[name] = "กรุณาเลือกช่วงรายได้";
        else delete newErrors[name];
        break;
      case "Education": // New validation
        if (!value) newErrors[name] = "กรุณาเลือกการศึกษา";
        else delete newErrors[name];
        break;
      case "Occupation":
        if (!value) newErrors[name] = "กรุณาเลือกอาชีพ";
        else if (value === "อื่นๆ" && !formData.OtherOccupation) newErrors["OtherOccupation"] = "กรุณาระบุอาชีพ";
        else {
          delete newErrors[name];
          delete newErrors["OtherOccupation"];
        }
        break;
      case "OtherOccupation":
        if (formData.Occupation === "อื่นๆ" && !value) newErrors[name] = "กรุณาระบุอาชีพ";
        else delete newErrors[name];
        break;
      case "VaccineTypeID":
      case "LivestockTypeID":
      case "UsageDate":
        if (!value) newErrors[name] = "กรุณาเลือกข้อมูล";
        else delete newErrors[name];
        break;
      case "SelectedProducts":
        if (!value || value.length === 0) newErrors[name] = "กรุณาเลือกผลิตภัณฑ์อย่างน้อย 1 รายการ";
        else delete newErrors[name];
        break;
      case "SatisfactionRatings":
        const missingProductRatings = Object.values(value).some((rating) => !rating);
        if (missingProductRatings) newErrors[name] = "กรุณาให้คะแนนความพึงพอใจของผลิตภัณฑ์ทุกข้อ";
        else delete newErrors[name];
        break;
      case "ServiceSatisfactionRatings":
        const missingServiceRatings = Object.values(value).some((rating) => !rating);
        if (missingServiceRatings) newErrors[name] = "กรุณาให้คะแนนความพึงพอใจต่อการให้บริการทุกข้อ";
        else delete newErrors[name];
        break;
      case "CompareSatisfactionRatings": // New validation
        const missingCompareRatings = Object.values(value).some((rating) => !rating);
        if (missingCompareRatings) newErrors[name] = "กรุณาให้คะแนนความพึงพอใจเมื่อเทียบกับวัคซีนเอกชนทุกข้อ";
        else delete newErrors[name];
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    // Manually trigger validation for all fields before final check
    const fieldsToValidate = [
      "FullName", "Phone", "Email", "Gender", "Age", "Occupation", "Income", "Education",
      //"VaccineTypeID", "LivestockTypeID", "UsageDate", "SelectedProducts",
      "SatisfactionRatings", "ServiceSatisfactionRatings", "CompareSatisfactionRatings"
    ];

    let currentErrors = {};
    fieldsToValidate.forEach(field => {
      validateField(field, formData[field]);
      if (errors[field]) { // Check if errors exist after validation
        currentErrors[field] = errors[field];
      }
    });

    // Special handling for OtherOccupation
    if (formData.Occupation === "อื่นๆ" && !formData.OtherOccupation) {
      currentErrors["OtherOccupation"] = "กรุณาระบุอาชีพ";
    } else {
      delete currentErrors["OtherOccupation"];
    }

    setErrors(currentErrors); // Update errors state

    // Check if there are any errors
    return Object.keys(currentErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วนและถูกต้อง");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/submitVaccineQuestionnaire",
        data: {
          ...formData,
          CreateBy: formData.FullName,
          // Pass specific suggestion fields
          SuggestionsProductQuality: formData.SuggestionsProductQuality,
          SuggestionsPrice: formData.SuggestionsPrice,
          SuggestionsService: formData.SuggestionsService,
          SuggestionsPR: formData.SuggestionsPR,
          // The main 'Suggestions' field is for "ข้อเสนอแนะอื่นๆ"
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("ขอขอบคุณที่ร่วมทำแบบสำรวจความพึงพอใจในผลิตภัณฑ์ เราจะนำข้อมูลไปปรับปรุงการให้บริการ");
        handleCancel();
        navigate("/home");
      } else {
        alert("เกิดข้อผิดพลาด: " + response.error);
      }
    } catch (error) {
      console.error("Error submitting questionnaire:", error);
      alert("เกิดข้อผิดพลาดในการส่งแบบสำรวจความพึงพอใจในผลิตภัณฑ์ \r\r" + error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      FullName: "",
      Phone: "",
      Email: "",
      Gender: "",
      Age: "",
      Occupation: "",
      OtherOccupation: "",
      Income: "",
      Education: "",
      VaccineTypeID: "",
      LivestockTypeID: "",
      UsageDate: "",
      Remarks: "",
      SelectedProducts: [],
      SatisfactionRatings: {
        ProductQuality: "",
        ProductSafety: "",
        PriceQuality: "",
        ConvenienceSpeed: "",
        DiseasePrevention: "",
        ProductSuitability: "",
        OverallProductSatisfaction: "",
      },
      ServiceSatisfactionRatings: {
        ReadyOfService: "",
        KnowledgeOfService: "",
        SpeedOfService: "",
        ProcessOfService: "",
        InfoOfService: "",
      },
      CompareSatisfactionRatings: {
        ComfortOfCompare: "",
        KnowledgeOfCompare: "",
        PriceOfCompare: "",
      },
      SuggestionsProductQuality: "",
      SuggestionsPrice: "",
      SuggestionsService: "",
      SuggestionsPR: "",
      Suggestions: "",
    });
    setErrors({});
  };

  if (!sessionId) {
    return null;
  }

  const satisfactionOptions = [
    { value: "5", label: "5 (มากที่สุด)" },
    { value: "4", label: "4 (มาก)" },
    { value: "3", label: "3 (ปานกลาง)" },
    { value: "2", label: "2 (น้อย)" },
    { value: "1", label: "1 (น้อยที่สุด)" },
  ];

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading
              as="h1"
              size={{ base: "md", md: "lg" }}
              fontWeight="normal"
            >
              แบบสำรวจความพึงพอใจในผลิตภัณฑ์
            </Heading>
          </HStack>
        </Box>

        <Box
          p={5}
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          w="100%"
          bg="white"
        >
          {/* Section 1: ข้อมูลส่วนตัวและการใช้งานวัคซีน */}
          <Heading size="md" mb={4}>
            ตอนที่ 1: ข้อมูลส่วนตัวและการใช้งานวัคซีน
          </Heading>
          <Grid templateColumns="repeat(4, 1fr)" gap={6}>
            {/* FullName, Phone, Email fields from original form, moved to top for clarity */}
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.FullName}>
                <FormLabel>ชื่อ-นามสกุล</FormLabel>
                <Input
                  name="FullName"
                  value={formData.FullName}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FullName}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 4, md: 1 }}>
              <FormControl isRequired isInvalid={!!errors.Phone}>
                <FormLabel>หมายเลขโทรศัพท์</FormLabel>
                <Input
                  name="Phone"
                  value={formData.Phone}
                  onChange={handleInputChange}
                  type="tel"
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.Phone}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 4, md: 1 }}>
              <FormControl isRequired isInvalid={!!errors.Email}>
                <FormLabel>อีเมล</FormLabel>
                <Input
                  name="Email"
                  value={formData.Email}
                  onChange={handleInputChange}
                  type="email"
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.Email}</FormErrorMessage>
              </FormControl>
            </GridItem>
            {/* End of moved fields */}

            <GridItem colSpan={2}>
              <FormControl isRequired isInvalid={!!errors.Gender}>
                <FormLabel>เพศ</FormLabel>
                <RadioGroup
                  name="Gender"
                  value={formData.Gender}
                  onChange={(value) => handleRadioChange("Gender", value)}
                >
                  <HStack spacing={4}>
                    <Radio value="M">ชาย</Radio>
                    <Radio value="F">หญิง</Radio>
                  </HStack>
                </RadioGroup>
                <FormErrorMessage>{errors.Gender}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl isRequired isInvalid={!!errors.Age}>
                <FormLabel>อายุ</FormLabel>
                <RadioGroup
                  name="Age"
                  value={formData.Age}
                  onChange={(value) => handleRadioChange("Age", value)}
                >
                  <HStack align="start" spacing={30}>
                    <Radio value="Under20">ต่ำกว่า 20 ปี</Radio>
                    <Radio value="20-30">20-30 ปี</Radio>
                    <Radio value="31-40">31-40 ปี</Radio>
                    <Radio value="41Plus">41 ปีขึ้นไป</Radio>
                  </HStack>
                </RadioGroup>
                <FormErrorMessage>{errors.Age}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl isRequired isInvalid={!!errors.Income}>
                <FormLabel>รายได้ต่อเดือน</FormLabel>
                <RadioGroup
                  name="Income"
                  value={formData.Income}
                  onChange={(value) => handleRadioChange("Income", value)}
                >
                  <HStack align="start" spacing={30}>
                    <Radio value="Under10K">ต่ำกว่า 10,000 บาท</Radio>
                    <Radio value="10K-20K">10,000 - 20,000 บาท</Radio>
                    <Radio value="20KPlus">มากกว่า 20,000 บาทขึ้นไป</Radio>
                  </HStack>
                </RadioGroup>
                <FormErrorMessage>{errors.Income}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl isRequired isInvalid={!!errors.Education}>
                <FormLabel>การศึกษา</FormLabel>
                <RadioGroup
                  name="Education"
                  value={formData.Education}
                  onChange={(value) => handleRadioChange("Education", value)}
                >
                  <HStack align="start" spacing={30}>
                    <Radio value="UnderGraduate">ต่ำกว่าปริญญาตรี</Radio>
                    <Radio value="Bachelor">ปริญญาตรี</Radio>
                    <Radio value="PostGraduate">สูงกว่าปริญญาตรี</Radio>
                  </HStack>
                </RadioGroup>
                <FormErrorMessage>{errors.Education}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 4, md: 2 }}>
              <FormControl isRequired isInvalid={!!errors.Occupation}>
                <FormLabel>อาชีพ</FormLabel>
                <Select
                  name="Occupation"
                  value={formData.Occupation}
                  onChange={handleInputChange}
                  placeholder="เลือกอาชีพ"
                  colorScheme="iBlue"
                >
                  <option value="เกษตรกร">เกษตรกร</option>
                  <option value="ผู้ประกอบการ">ผู้ประกอบการ</option>
                  <option value="เจ้าหน้าที่กรมปศุสัตว์">เจ้าหน้าที่กรมปศุสัตว์</option>
                  <option value="อื่นๆ">อื่นๆ</option>
                </Select>
                <FormErrorMessage>{errors.Occupation}</FormErrorMessage>
              </FormControl>
              {formData.Occupation === "อื่นๆ" && (
                <FormControl mt={2} isRequired isInvalid={!!errors.OtherOccupation}>
                  <FormLabel>ระบุอาชีพ</FormLabel>
                  <Input
                    name="OtherOccupation"
                    value={formData.OtherOccupation}
                    onChange={handleInputChange}
                    colorScheme="iBlue"
                  />
                  <FormErrorMessage>{errors.OtherOccupation}</FormErrorMessage>
                </FormControl>
              )}
            </GridItem>
            {/* Removed duplicated Phone and Email fields */}
            
            <GridItem colSpan={2}> </GridItem>  
            <GridItem colSpan={{ base: 4, md: 1 }}>
              <FormControl isRequired isInvalid={!!errors.VaccineTypeID}>
                <FormLabel>ประเภทวัคซีน</FormLabel>
                <Select
                  name="VaccineTypeID"
                  value={formData.VaccineTypeID}
                  onChange={handleInputChange}
                  placeholder="เลือกประเภทวัคซีน"
                  colorScheme="iBlue"
                >
                  {vaccineTypes.map((vaccine) => (
                    <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.VaccineTypeID}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 4, md: 1 }}>
              <FormControl isRequired isInvalid={!!errors.LivestockTypeID}>
                <FormLabel>ประเภทปศุสัตว์</FormLabel>
                <Select
                  name="LivestockTypeID"
                  value={formData.LivestockTypeID}
                  onChange={handleInputChange}
                  placeholder="เลือกประเภทปศุสัตว์"
                  colorScheme="iBlue"
                >
                  {livestockTypes.map((type) => (
                    <option
                      key={type.LivestockTypeID}
                      value={type.LivestockTypeID}
                    >
                      {type.LivestockTypeName}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{errors.LivestockTypeID}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 4, md: 1 }}>
              <FormControl isRequired isInvalid={!!errors.UsageDate}>
                <FormLabel>วันที่ใช้งานวัคซีน</FormLabel>
                <CustomDatePicker
                  name="UsageDate"
                  value={formData.UsageDate}
                  onChange={handleInputChange}
                  maxDate={new Date()} 
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.UsageDate}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={4}>
              <FormControl>
                <FormLabel>หมายเหตุ</FormLabel>
                <Textarea
                  name="Remarks"
                  value={formData.Remarks}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
          </Grid>

          {/* Section 2: ผลิตภัณฑ์ที่รับบริการ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 2: ผลิตภัณฑ์ที่รับบริการ (เลือกได้มากกว่า 1 รายการ)
          </Heading>
          <FormControl isRequired isInvalid={!!errors.SelectedProducts}>
            <CheckboxGroup
              value={formData.SelectedProducts}
              onChange={handleProductChange}
            >
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                {vaccineTypes.map((vaccine) => (
                  <GridItem key={vaccine.VaccineID}>
                    <Checkbox value={vaccine.VaccineID}>
                      {vaccine.VaccineNameTH}
                    </Checkbox>
                  </GridItem>
                ))}
              </Grid>
            </CheckboxGroup>
            <FormErrorMessage>{errors.SelectedProducts}</FormErrorMessage>
          </FormControl>

          {/* Section 3.1: ความพึงพอใจของผลิตภัณฑ์ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 3: ความพึงพอใจต่อผลิตภัณฑ์ และคุณภาพการให้บริการ
          </Heading>
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 3.1: ด้านผลิตภัณฑ์ในการผลิตตามมาตรฐานสากล (OIE)
          </Heading>
          <FormControl isRequired isInvalid={!!errors.SatisfactionRatings}>
            <Table variant="striped" colorScheme="gray">
              <Thead>
                <Tr>
                  <Td>รายการ</Td>
                  <Td>ความพึงพอใจ</Td>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>1. คุณภาพของวัคซีน (ความคุ้มโรค)</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.ProductQuality}
                      onChange={(e) =>
                        handleSatisfactionChange("ProductQuality", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>2. ความปลอดภัยของวัคซีน (การแพ้วัคซีน, อาการข้างเคียงอื่นๆ)</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.ProductSafety}
                      onChange={(e) =>
                        handleSatisfactionChange("ProductSafety", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>3. ราคาวัคซีน / โด๊ส</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.PriceQuality}
                      onChange={(e) =>
                        handleSatisfactionChange("PriceQuality", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>4. ขนาดบรรจุ / ขวด</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.ConvenienceSpeed}
                      onChange={(e) =>
                        handleSatisfactionChange("ConvenienceSpeed", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>5. ความเหมาะสมของบรรจุภัณฑ์</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.DiseasePrevention}
                      onChange={(e) =>
                        handleSatisfactionChange("DiseasePrevention", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>6. ปริมาณวัคซีนเพียงพอต่อความต้องการ</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.ProductSuitability}
                      onChange={(e) =>
                        handleSatisfactionChange("ProductSuitability", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>7. ความพึงพอใจเมื่อเทียบกับวัคซีนที่จำหน่ายโดยเอกชน</Td>
                  <Td>
                    <Select
                      value={formData.SatisfactionRatings.OverallProductSatisfaction}
                      onChange={(e) =>
                        handleSatisfactionChange("OverallProductSatisfaction", e.target.value)
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <FormErrorMessage>{errors.SatisfactionRatings}</FormErrorMessage>
          </FormControl>

          {/* Section 3.2: ความพึงพอใจต่อการให้บริการ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 3.2: ด้านการบริการต่อลูกค้า
          </Heading>
          <FormControl isRequired isInvalid={!!errors.ServiceSatisfactionRatings}>
            <Table variant="striped" colorScheme="gray">
              <Thead>
                <Tr>
                  <Td>รายการ</Td>
                  <Td>ความพึงพอใจ</Td>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>1. เจ้าหน้าที่มีความพร้อมในการให้บริการจำหน่ายวัคซีน</Td>
                  <Td>
                    <Select
                      value={formData.ServiceSatisfactionRatings.ReadyOfService}
                      onChange={(e) =>
                        handleSatisfactionChange("ReadyOfService", e.target.value, "ServiceSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>2. เจ้าหน้าที่มีความรู้ความสามารถ และสามารถให้คำปรึกษาได้อย่างชัดเจน</Td>
                  <Td>
                    <Select
                      value={formData.ServiceSatisfactionRatings.KnowledgeOfService}
                      onChange={(e) =>
                        handleSatisfactionChange("KnowledgeOfService", e.target.value, "ServiceSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>3. เจ้าหน้าที่ให้บริการ รวดเร็ว ทันต่อความต้องการ</Td>
                  <Td>
                    <Select
                      value={formData.ServiceSatisfactionRatings.SpeedOfService}
                      onChange={(e) =>
                        handleSatisfactionChange("SpeedOfService", e.target.value, "ServiceSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>4. มีขั้นตอนในการให้บริการที่ชัดเจนและสะดวกต่อการเข้ารับบริการ</Td>
                  <Td>
                    <Select
                      value={formData.ServiceSatisfactionRatings.ProcessOfService}
                      onChange={(e) =>
                        handleSatisfactionChange("ProcessOfService", e.target.value, "ServiceSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>5. การเข้าถึงข้อมูลข่าวสาร พร้อมทั้งสื่อประชาสัมพันธ์ สะดวกครบถ้วน และเป็นปัจจุบัน</Td>
                  <Td>
                    <Select
                      value={formData.ServiceSatisfactionRatings.InfoOfService}
                      onChange={(e) =>
                        handleSatisfactionChange("InfoOfService", e.target.value, "ServiceSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <FormErrorMessage>{errors.ServiceSatisfactionRatings}</FormErrorMessage>
          </FormControl>


          {/* Section 3.3: ความพึงพอใจเมื่อเทียบกับวัคซีนที่จำหน่ายโดยเอกชน */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 3.3: ความพึงพอใจเมื่อเทียบกับวัคซีนที่จำหน่ายโดยเอกชน
          </Heading>
          <FormControl isRequired isInvalid={!!errors.CompareSatisfactionRatings}>
            <Table variant="striped" colorScheme="gray">
              <Thead>
                <Tr>
                  <Td>รายการ</Td>
                  <Td>ความพึงพอใจ</Td>
                </Tr>
              </Thead>
              <Tbody>
                <Tr>
                  <Td>1. ด้านสิ่งอำนวยความสะดวก ช่องทางการสั่งซื้อวัคซีน</Td>
                  <Td>
                    <Select
                      value={formData.CompareSatisfactionRatings.ComfortOfCompare}
                      onChange={(e) =>
                        handleSatisfactionChange("ComfortOfCompare", e.target.value, "CompareSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>2. ด้านการให้บริการ เจ้าหน้าที่มีความรู้ความสามารถ และสามารถให้คำปรึกษาได้อย่างชัดเจน</Td>
                  <Td>
                    <Select
                      value={formData.CompareSatisfactionRatings.KnowledgeOfCompare}
                      onChange={(e) =>
                        handleSatisfactionChange("KnowledgeOfCompare", e.target.value, "CompareSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
                <Tr>
                  <Td>3. ด้านราคาวัคซีน / โด๊ส</Td>
                  <Td>
                    <Select
                      value={formData.CompareSatisfactionRatings.PriceOfCompare}
                      onChange={(e) =>
                        handleSatisfactionChange("PriceOfCompare", e.target.value, "CompareSatisfactionRatings")
                      }
                      placeholder="เลือกคะแนน"
                    >
                      {satisfactionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Td>
                </Tr>
              </Tbody>
            </Table>
            <FormErrorMessage>{errors.CompareSatisfactionRatings}</FormErrorMessage>
          </FormControl>

          {/* Section 4: ข้อเสนอแนะ ที่ต้องการให้ปรับปรุง */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 4: ข้อเสนอแนะ ที่ต้องการให้ปรับปรุง
          </Heading>
          <FormControl>
            <FormLabel>คุณภาพของวัคซีน - รายละเอียดที่ต้องการให้ปรับปรุง</FormLabel>
            <Textarea
              name="SuggestionsProductQuality" // New name
              value={formData.SuggestionsProductQuality}
              onChange={handleInputChange}
              colorScheme="iBlue"
            />
          </FormControl>
          <FormControl>
            <FormLabel>ราคา - รายละเอียดที่ต้องการให้ปรับปรุง</FormLabel>
            <Textarea
              name="SuggestionsPrice" // New name
              value={formData.SuggestionsPrice}
              onChange={handleInputChange}
              colorScheme="iBlue"
            />
          </FormControl>
          <FormControl>
            <FormLabel>การให้บริการ - รายละเอียดที่ต้องการให้ปรับปรุง</FormLabel>
            <Textarea
              name="SuggestionsService" // New name
              value={formData.SuggestionsService}
              onChange={handleInputChange}
              colorScheme="iBlue"
            />
          </FormControl>
          <FormControl>
            <FormLabel>การประชาสัมพันธ์ - รายละเอียดที่ต้องการให้ปรับปรุง</FormLabel>
            <Textarea
              name="SuggestionsPR" // New name
              value={formData.SuggestionsPR}
              onChange={handleInputChange}
              colorScheme="iBlue"
            />
          </FormControl>

          
          {/* Section 5: ข้อเสนอแนะอื่นๆ */}
          <Heading size="md" mt={8} mb={4}>
            ตอนที่ 5: ข้อเสนอแนะอื่นๆ
          </Heading>
          <FormControl>
            <FormLabel>ข้อเสนอแนะอื่นๆ</FormLabel>
            <Textarea
              name="Suggestions" // This is now for "ข้อเสนอแนะอื่นๆ"
              value={formData.Suggestions}
              onChange={handleInputChange}
              colorScheme="iBlue"
            />
          </FormControl>

          {/* Buttons */}
          <HStack mt={6} justify="flex-end">
            <Cancel onClick={handleCancel} label="ยกเลิก" />
            <Save onClick={handleSubmit} isLoading={isLoading} label="ส่งแบบสอบถาม" />
          </HStack>
        </Box>
      </VStack>
    </Box>
  );
};

export default VaccineQuestionnaireForm;
